import { isDevHost } from '../firebase.config.js';

/** Default bildit-cms branch used for local signup deploys. */
export const DEFAULT_DEPLOYMENT_BRANCH = 'release/2.0.0';

/** Fallback branches when the legacy pipeline runner is unavailable. */
export const FALLBACK_DEPLOYMENT_BRANCHES = [
  'release/2.0.0',
  'release/2.0.1',
  'release/1.2.8',
  'develop',
];

const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const BRANCH_STORAGE_KEY = 'bildit.localDeploymentBranch';
const RUNNER_BASE_URL = 'http://127.0.0.1:8090';
const SITE_STEM_MAX_LENGTH = 13;
const LOCAL_DEV_SUFFIX_LENGTH = 4;

/**
 * True when the signup page is served from localhost (not dev-roi-tool.web.app).
 * @return {boolean}
 */
export function isLocalhostSignup() {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname;
  return LOCAL_HOSTS.some((pattern) => host.includes(pattern));
}

/**
 * True when the signup page is served from dev hosting (not localhost).
 * Includes dev-roi-tool.web.app and custom dev domains like dev.signup.bildit.co.
 * @return {boolean}
 */
export function isDevRoiToolSignup() {
  return isDevHost() && !isLocalhostSignup();
}

/**
 * True when the CMS deploy branch field should be shown (localhost or dev hosting).
 * @return {boolean}
 */
export function showsDeploymentBranchField() {
  return isDevHost();
}

/**
 * Build a bildit-* site id from raw user input.
 * On localhost, appends a random suffix to avoid Firebase Hosting global collisions.
 * @param {string} rawSiteName
 * @return {string}
 */
export function buildBilditSiteName(rawSiteName) {
  let stem = String(rawSiteName || '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

  if (isLocalhostSignup()) {
    const suffix = Math.random()
      .toString(36)
      .replace(/[^a-z0-9]/g, '')
      .slice(2, 2 + LOCAL_DEV_SUFFIX_LENGTH);
    const stemMax = SITE_STEM_MAX_LENGTH - suffix.length;
    stem = `${stem.substring(0, stemMax)}${suffix}`;
  } else {
    stem = stem.substring(0, SITE_STEM_MAX_LENGTH);
  }

  return `bildit-${stem}`;
}

/**
 * @return {string}
 */
export function getStoredDeploymentBranch() {
  if (typeof window === 'undefined') {
    return DEFAULT_DEPLOYMENT_BRANCH;
  }
  return (
    window.localStorage.getItem(BRANCH_STORAGE_KEY) || DEFAULT_DEPLOYMENT_BRANCH
  );
}

/**
 * @param {string} branch
 */
export function storeDeploymentBranch(branch) {
  if (typeof window === 'undefined' || !branch) {
    return;
  }
  window.localStorage.setItem(BRANCH_STORAGE_KEY, branch);
}

/**
 * @return {Promise<string[]>}
 */
export async function fetchDeploymentBranches() {
  if (!isLocalhostSignup()) {
    return FALLBACK_DEPLOYMENT_BRANCHES;
  }

  try {
    const response = await fetch(`${RUNNER_BASE_URL}/branches`);
    if (!response.ok) {
      throw new Error(`Branch list failed (${response.status})`);
    }
    const data = await response.json();
    if (Array.isArray(data.branches) && data.branches.length > 0) {
      return data.branches;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[localDevSignup] Could not load branches from runner:', error);
  }

  return FALLBACK_DEPLOYMENT_BRANCHES;
}
